import React, { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, Box, Typography } from '@mui/material';
import { Download, Close, GetApp } from '@mui/icons-material';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if app is already installed/running as PWA
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone || 
                        document.referrer.includes('android-app://');
      setIsStandalone(standalone);
    };

    checkStandalone();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA install prompt available');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Don't show if already dismissed
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed && !isStandalone) {
        setShowBanner(true);
      }
    };

    // Listen for app installation
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setShowBanner(false);
      setDeferredPrompt(null);
      localStorage.removeItem('pwa-install-dismissed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Show install prompt after some time if conditions are met
    const timer = setTimeout(() => {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed && !isStandalone && window.location.protocol === 'https:') {
        setShowInstallPrompt(true);
      }
    }, 10000); // Show after 10 seconds

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(timer);
    };
  }, [isStandalone]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      if (outcome === 'dismissed') {
        localStorage.setItem('pwa-install-dismissed', 'true');
      }
      
      setDeferredPrompt(null);
      setShowBanner(false);
      setShowInstallPrompt(false);
    } else {
      // Fallback for browsers that don't support the install prompt
      alert('To install RackStar as an app:\n\n' +
            'Chrome/Edge: Click the ⊕ icon in the address bar\n' +
            'Firefox: Look for "Install" in the address bar\n' +
            'Safari: Share → Add to Home Screen');
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show anything if already installed
  if (isStandalone) {
    return null;
  }

  return (
    <>
      {/* Install Banner */}
      {showBanner && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
            color: 'white',
            padding: 2,
            borderRadius: 2,
            boxShadow: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            maxWidth: 350,
            zIndex: 1300,
          }}
        >
          <GetApp />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Install RackStar
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Install as a Windows app for better performance
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="inherit"
              onClick={handleInstallClick}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Install
            </Button>
            <Button
              size="small"
              onClick={handleDismiss}
              sx={{ 
                color: 'white',
                minWidth: 'auto',
                p: 0.5
              }}
            >
              <Close fontSize="small" />
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar notification */}
      <Snackbar
        open={showInstallPrompt}
        onClose={handleDismiss}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleDismiss}
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleInstallClick}
              startIcon={<Download />}
            >
              Install App
            </Button>
          }
        >
          Install RackStar as a Windows app for offline access and better performance!
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstallPrompt;
